import React, { useEffect, useState } from 'react';
import AdminHeader from '../components/AdminHeader';
import ConfirmModal from '../components/ConfirmModal';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { escapeCSV, downloadCSV, formatDateTime } from '../utils/csvExport';
import '../styles/AdminPromotions.css';
import './AdminCustomersPage_Mobile.css';

function AdminCustomersPage() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [deletedUsers, setDeletedUsers] = useState([]);
  const [unverifiedUsers, setUnverifiedUsers] = useState([]);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [userType, setUserType] = useState('customers');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteCustomerData, setDeleteCustomerData] = useState(null);
  const customersPerPage = 12;
  const token = localStorage.getItem('token');
  const isAdmin = localStorage.getItem('isAdmin') === 'true';

  useEffect(() => {
    const fetchCustomers = async () => {
      if (!token || !isAdmin) return setError('Not authorized');
      try {
        setLoading(true);
        const res = await axios.get(`${process.env.REACT_APP_API}/api/users/admin/customers`, { headers: { Authorization: `Bearer ${token}` } });
        setStats(res.data.stats || null);
        setAllUsers(res.data.users || []);
        setUsers(res.data.users || []);
        setError(null);
      } catch (err) {
        console.error(err);
        setError(err.response?.data?.message || 'Failed to fetch customers');
      } finally {
        setLoading(false);
      }
    };
    fetchCustomers();
  }, [token, isAdmin]);

  // Prefetch counts for Deleted and Unverified to display numbers on tabs
  useEffect(() => {
    if (!token || !isAdmin) return;
    (async () => {
      try { await fetchDeleted(); } catch {}
      try { await fetchUnverified(); } catch {}
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isAdmin]);

  const fetchDeleted = async () => {
    if (deletedUsers.length > 0) return; // cached
    try {
      const res = await axios.get(`${process.env.REACT_APP_API}/api/users/admin/customers/deleted`, { headers: { Authorization: `Bearer ${token}` } });
      setDeletedUsers(res.data.users || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to fetch deleted users');
    }
  };

  const fetchUnverified = async () => {
    if (unverifiedUsers.length > 0) return; // cached
    try {
      const res = await axios.get(`${process.env.REACT_APP_API}/api/users/admin/customers/unverified`, { headers: { Authorization: `Bearer ${token}` } });
      setUnverifiedUsers(res.data.users || []);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to fetch unverified users');
    }
  };

  // Filter users based on userType selection
  useEffect(() => {
    if (userType === 'customers') {
      // Only show verified customers (exclude unverified and admins)
      setUsers(allUsers.filter(u => !u.isAdmin && u.isEmailVerified));
    } else if (userType === 'admins') {
      setUsers(allUsers.filter(u => u.isAdmin));
    } else if (userType === 'deleted') {
      fetchDeleted();
      setUsers(deletedUsers);
    } else if (userType === 'unverified') {
      fetchUnverified();
      setUsers(unverifiedUsers);
    }
    setCurrentPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType, allUsers, deletedUsers, unverifiedUsers]);

  const confirmDeleteCustomer = async () => {
    if (!deleteCustomerData) return;
    try {
      const res = await axios.delete(`${process.env.REACT_APP_API}/api/users/admin/customers/${deleteCustomerData.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.status !== 200) throw new Error(res.data?.message || 'Failed to delete customer');
      setSelectedCustomer(null);
      // Refetch list and stats
      try {
        const ref = await axios.get(`${process.env.REACT_APP_API}/api/users/admin/customers`, { headers: { Authorization: `Bearer ${token}` } });
        setStats(ref.data.stats || null);
        setAllUsers(ref.data.users || []);
        setUsers((ref.data.users || []).filter(u => (userType === 'customers' ? !u.isAdmin : userType === 'admins' ? u.isAdmin : true)));
      } catch {}
      // Also refresh deleted cache so tab counts update
      try { await fetchDeleted(); } catch {}
    } catch (err) {
      console.error('Delete customer error:', err);
    }
    setDeleteCustomerData(null);
  };

  const exportToCSV = () => {
    let csvContent = 'Name,Email,Points,Is Admin,Verified,Created Date\n';
    
    filteredUsers.forEach(user => {
      const name = escapeCSV(user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'N/A');
      const email = escapeCSV(user.email || '');
      const points = escapeCSV(user.points || 0);
      const isAdmin = escapeCSV(user.isAdmin ? 'Yes' : 'No');
      const verified = escapeCSV(user.verified ? 'Yes' : 'No');
      const created = escapeCSV(user.createdAt ? formatDateTime(user.createdAt) : '');
      
      csvContent += `${name},${email},${points},${isAdmin},${verified},${created}\n`;
    });
    
    const filename = `customers_backup_${new Date().toISOString().split('T')[0]}.csv`;
    downloadCSV(csvContent, filename);
  };

  if (!isAdmin) return (
    <div>
      <AdminHeader />
      <div style={{ padding: 40 }}>You are not authorized to view this page.</div>
    </div>
  );

  if (loading) {
    return (
      <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Green Header Bar */}
        <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }} />
        
        {/* Navbar */}
        <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
          <div style={{ width: '100%', margin: '0 auto', background: 'transparent' }}>
            <AdminHeader />
          </div>
        </div>

        {/* Loading State */}
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1, rotate: 360 }}
            transition={{ duration: 0.6, rotate: { duration: 1, repeat: Infinity, ease: 'linear' } }}
            style={{ fontSize: '4rem' }}
          >
            🌱
          </motion.div>
        </div>
      </div>
    );
  }

  // Filter customers based on search query
  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.name?.toLowerCase().includes(query) ||
      user.email?.toLowerCase().includes(query) ||
      user.points?.toString().includes(query)
    );
  });

  // Pagination logic
  const totalPages = Math.ceil(filteredUsers.length / customersPerPage);
  const startIndex = (currentPage - 1) * customersPerPage;
  const endIndex = startIndex + customersPerPage;
  const currentCustomers = filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when search changes
  const handleSearchChange = (value) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  return (
    <div style={{ background: '#f8f8f8', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <div style={{ width: '100%', background: '#175c36', minHeight: 32, height: 32, position: 'sticky', top: 0, zIndex: 1001, boxShadow: '0 2px 8px rgba(23, 92, 54, 0.2)' }} />
      <div style={{ width: '100%', background: 'rgba(255, 255, 255, 0.95)', backdropFilter: 'blur(10px)', boxShadow: '0 2px 16px rgba(0,0,0,0.06)', position: 'sticky', top: 32, zIndex: 1000 }}>
        <div style={{ width: '100%', margin: '0 auto', background: 'transparent' }}>
          <AdminHeader />
        </div>
      </div>
      <div style={{ width: '100%', margin: '40px auto', padding: '0 20px', flex: 1, boxSizing: 'border-box' }}>
        <div className="admin-promotions">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 16 }}>
            <motion.h1 
              className="admin-customers-title"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              style={{ margin: 0 }}
            >
              Customers
            </motion.h1>
            <motion.button
              onClick={exportToCSV}
              style={{
                background: '#2e7d32',
                color: '#fff',
                padding: '10px 20px',
                borderRadius: '8px',
                border: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                boxShadow: '0 2px 8px rgba(46,125,50,0.2)'
              }}
              whileHover={{ scale: 1.02, backgroundColor: '#1b5e20' }}
              whileTap={{ scale: 0.98 }}
            >
              📊 Export CSV
            </motion.button>
          </div>
          
          {error && (
            <motion.div 
              className="error-message customers-error-message"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
            >
              {error}
            </motion.div>
          )}

          {/* Mobile Search Bar */}
          <div className="customers-search-section">
            <motion.div 
              className="customers-search-wrapper"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
            >
              <span className="customers-search-icon">🔍</span>
              <input
                type="text"
                className="customers-search-input"
                placeholder="Search by name, email, or points..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                aria-label="Search customers"
              />
            </motion.div>
          </div>

          {/* User Type Filter */}
          <motion.div 
            style={{ 
              display: 'flex', 
              gap: 8, 
              marginBottom: 20, 
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.15 }}
          >
            

            {/* Customers */}
            <motion.button
              onClick={() => setUserType('customers')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '10px 24px',
                borderRadius: 24,
                border: userType === 'customers' ? '2px solid #7a9c3a' : '2px solid #ddd',
                background: userType === 'customers' ? '#7a9c3a' : '#fff',
                color: userType === 'customers' ? '#fff' : '#666',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              👥 Customers ({stats?.totalUsers ?? 0})
            </motion.button>

            {/* Admins */}
            <motion.button
              onClick={() => setUserType('admins')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '10px 24px',
                borderRadius: 24,
                border: userType === 'admins' ? '2px solid #7a9c3a' : '2px solid #ddd',
                background: userType === 'admins' ? '#7a9c3a' : '#fff',
                color: userType === 'admins' ? '#fff' : '#666',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              🛡️ Admins ({stats?.adminCount ?? 0})
            </motion.button>

            {/* Unverified */}
            <motion.button
              onClick={() => setUserType('unverified')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '10px 24px',
                borderRadius: 24,
                border: userType === 'unverified' ? '2px solid #7a9c3a' : '2px solid #ddd',
                background: userType === 'unverified' ? '#7a9c3a' : '#fff',
                color: userType === 'unverified' ? '#fff' : '#666',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              ✉️ Unverified ({unverifiedUsers.length || 0})
            </motion.button>

            {/* Deleted */}
            <motion.button
              onClick={() => setUserType('deleted')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              style={{
                padding: '10px 24px',
                borderRadius: 24,
                border: userType === 'deleted' ? '2px solid #b91c1c' : '2px solid #ddd',
                background: userType === 'deleted' ? '#dc2626' : '#fff',
                color: userType === 'deleted' ? '#fff' : '#666',
                fontWeight: 600,
                fontSize: '0.9rem',
                cursor: 'pointer',
                transition: 'all 0.2s'
              }}
            >
              🗑️ Deleted ({deletedUsers.length || 0})
            </motion.button>
          </motion.div>

          {/* Summary Cards */}
          <motion.div 
            className="customer-summary customers-summary-grid" 
            style={{ display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4, delay: 0.2 }}
          >
            <motion.div 
              className="admin-summary-card customers-summary-card" 
              style={{ flex: '1 1 200px', background: '#fff', padding: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="customers-summary-label" style={{ fontSize: 12, color: '#666' }}>Total Customers</div>
              <div className="customers-summary-value" style={{ fontSize: 28, fontWeight: 700 }}>{stats?.totalUsers ?? 0}</div>
            </motion.div>
            <motion.div 
              className="admin-summary-card customers-summary-card" 
              style={{ flex: '1 1 200px', background: '#fff', padding: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="customers-summary-label" style={{ fontSize: 12, color: '#666' }}>Total Admins</div>
              <div className="customers-summary-value" style={{ fontSize: 28, fontWeight: 700, color: '#7a9c3a' }}>{stats?.adminCount ?? 0}</div>
            </motion.div>
            <motion.div 
              className="admin-summary-card customers-summary-card" 
              style={{ flex: '1 1 200px', background: '#fff', padding: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="customers-summary-label" style={{ fontSize: 12, color: '#666' }}>Total Points</div>
              <div className="customers-summary-value" style={{ fontSize: 28, fontWeight: 700 }}>{stats?.totalPoints ?? 0}</div>
            </motion.div>
            <motion.div 
              className="admin-summary-card customers-summary-card" 
              style={{ flex: '1 1 200px', background: '#fff', padding: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="customers-summary-label" style={{ fontSize: 12, color: '#666' }}>Avg Points</div>
              <div className="customers-summary-value" style={{ fontSize: 28, fontWeight: 700 }}>{stats?.avgPoints ?? 0}</div>
            </motion.div>
            <motion.div 
              className="admin-summary-card customers-summary-card" 
              style={{ flex: '1 1 200px', background: '#fff', padding: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' }}
              whileTap={{ scale: 0.95 }}
            >
              <div className="customers-summary-label" style={{ fontSize: 12, color: '#666' }}>New (30d)</div>
              <div className="customers-summary-value" style={{ fontSize: 28, fontWeight: 700 }}>{stats?.newUsersLast30 ?? 0}</div>
            </motion.div>
          </motion.div>

          {/* Mobile Customer Cards */}
          <div className="customers-cards-container">
            {currentCustomers.length === 0 ? (
              <motion.div 
                className="customers-empty-state"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4 }}
              >
                <div className="customers-empty-icon">👥</div>
                <div className="customers-empty-title">No Customers Found</div>
                <div className="customers-empty-message">
                  {searchQuery ? 'Try adjusting your search criteria' : 'No customers registered yet'}
                </div>
              </motion.div>
            ) : (
              currentCustomers.map((user, idx) => (
                <motion.div
                  key={user.id}
                  className="customer-card"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.3, 
                    delay: idx * 0.05,
                    ease: [0.4, 0, 0.2, 1]
                  }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setSelectedCustomer(user)}
                >
                  <div className="customer-card-header">
                    <div className="customer-name-section">
                      <h3 className="customer-name">
                        {user.name}
                        {user.role === 'superadmin' && (
                          <span style={{
                            marginLeft: 8,
                            fontSize: '0.75rem',
                            background: '#fbbf24',
                            color: '#78350f',
                            padding: '3px 10px',
                            borderRadius: 12,
                            fontWeight: 600,
                            letterSpacing: '0.5px'
                          }}>
                            ⭐ SUPER ADMIN
                          </span>
                        )}
                        {user.isAdmin && user.role !== 'superadmin' && (
                          <span style={{
                            marginLeft: 8,
                            fontSize: '0.75rem',
                            background: 'linear-gradient(135deg, #7a9c3a 0%, #5e7c2a 100%)',
                            color: '#fff',
                            padding: '3px 10px',
                            borderRadius: 12,
                            fontWeight: 600,
                            letterSpacing: '0.5px'
                          }}>
                            🛡️ ADMIN
                          </span>
                        )}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <p className="customer-email" style={{ margin: 0 }}>{user.email}</p>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          fontSize: '0.65rem',
                          background: 'transparent',
                          color: user.isEmailVerified ? '#28a745' : '#856404',
                          border: `1px solid ${user.isEmailVerified ? '#28a745' : '#ffc107'}`,
                          borderRadius: 4,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}>
                          {user.isEmailVerified ? 'Verified' : 'Unverified'}
                        </span>
                      </div>
                    </div>
                    <div className="customer-points-badge">
                      {user.points}
                    </div>
                  </div>

                  <div className="customer-stats-grid">
                    <div className="customer-stat-item">
                      <div className="customer-stat-label">Addresses</div>
                      <div className="customer-stat-value">{user.addressesCount}</div>
                    </div>
                    <div className="customer-stat-item">
                      <div className="customer-stat-label">Vouchers</div>
                      <div className="customer-stat-value">{user.vouchersCount}</div>
                    </div>
                    <div className="customer-stat-item">
                      <div className="customer-stat-label">Orders</div>
                      <div className="customer-stat-value">{user.ordersCount || 0}</div>
                    </div>
                  </div>

                  <div className="customer-card-footer">
                    <div className="customer-joined-date">
                      {new Date(user.createdAt).toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })}
                    </div>
                    <motion.button 
                      className="customer-action-btn"
                      whileTap={{ scale: 0.95 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedCustomer(user);
                      }}
                    >
                      View Details
                    </motion.button>
                  </div>
                </motion.div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <motion.div 
              className="pagination-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.2 }}
            >
              <div className="pagination-info">
                Showing {startIndex + 1}-{Math.min(endIndex, filteredUsers.length)} of {filteredUsers.length} customers
              </div>
              <div className="pagination-controls">
                <motion.button 
                  className="page-btn"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Go to first page"
                >
                  First
                </motion.button>
                <motion.button 
                  className="page-btn"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Go to previous page"
                >
                  Previous
                </motion.button>
                
                {/* Page numbers */}
                {[...Array(totalPages)].map((_, index) => {
                  const pageNum = index + 1;
                  if (
                    pageNum === 1 || 
                    pageNum === totalPages || 
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <motion.button
                        key={pageNum}
                        className={`page-btn ${currentPage === pageNum ? 'active' : ''}`}
                        onClick={() => setCurrentPage(pageNum)}
                        whileTap={{ scale: 0.95 }}
                        aria-label={`Go to page ${pageNum}`}
                        aria-current={currentPage === pageNum ? 'page' : undefined}
                      >
                        {pageNum}
                      </motion.button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="page-ellipsis">...</span>;
                  }
                  return null;
                })}
                
                <motion.button 
                  className="page-btn"
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Go to next page"
                >
                  Next
                </motion.button>
                <motion.button 
                  className="page-btn"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                  whileTap={{ scale: 0.95 }}
                  aria-label="Go to last page"
                >
                  Last
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Desktop Table */}
          <div className="desktop-table-wrapper" style={{ background: '#fff', padding: 12, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.04)', overflowX: 'auto' }}>
            <table className="promo-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid #eee' }}>
                  <th style={{ padding: 8 }}>Name</th>
                  <th style={{ padding: 8 }}>Email</th>
                  <th style={{ padding: 8 }}>Status</th>
                  <th style={{ padding: 8 }}>Points</th>
                  <th style={{ padding: 8 }}>Addresses</th>
                  <th style={{ padding: 8 }}>Vouchers</th>
                  <th style={{ padding: 8 }}>Orders</th>
                  <th style={{ padding: 8 }}>Joined</th>
                </tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} style={{ borderBottom: '1px solid #f6f6f6' }}>
                    <td data-label="Name" style={{ padding: 10 }}>{u.name}</td>
                    <td data-label="Email" style={{ padding: 10 }}>{u.email}</td>
                    <td data-label="Status" style={{ padding: 10 }}>
                      <span style={{
                        display: 'inline-block',
                        padding: '3px 10px',
                        fontSize: '0.7rem',
                        background: 'transparent',
                        color: u.isEmailVerified ? '#28a745' : '#856404',
                        border: `1px solid ${u.isEmailVerified ? '#28a745' : '#ffc107'}`,
                        borderRadius: 4,
                        fontWeight: 500,
                        textTransform: 'uppercase',
                        letterSpacing: '0.3px'
                      }}>
                        {u.isEmailVerified ? 'Verified' : 'Unverified'}
                      </span>
                    </td>
                    <td data-label="Points" style={{ padding: 10 }}>{u.points}</td>
                    <td data-label="Addresses" style={{ padding: 10 }}>{u.addressesCount}</td>
                    <td data-label="Vouchers" style={{ padding: 10 }}>{u.vouchersCount}</td>
                    <td data-label="Orders" style={{ padding: 10 }}>{u.ordersCount || 0}</td>
                    <td data-label="Joined" style={{ padding: 10 }}>{new Date(u.createdAt).toLocaleDateString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Customer Detail Modal */}
      <AnimatePresence>
        {selectedCustomer && (
          <motion.div
            className="customer-detail-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setSelectedCustomer(null)}
          >
            <motion.div
              className="customer-detail-modal"
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ 
                duration: 0.4, 
                ease: [0.4, 0, 0.2, 1]
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="customer-detail-header">
                <h2 className="customer-detail-title">Customer Details</h2>
                <button 
                  className="customer-detail-close"
                  onClick={() => setSelectedCustomer(null)}
                  aria-label="Close modal"
                >
                  ✕
                </button>
              </div>

              <div className="customer-detail-body">
                <div className="customer-detail-section">
                  <h3 className="customer-detail-section-title">Personal Information</h3>
                  <div className="customer-detail-info-grid">
                    <div className="customer-detail-info-item">
                      <span className="customer-detail-info-label">Name</span>
                      <span className="customer-detail-info-value">{selectedCustomer.name}</span>
                    </div>
                    <div className="customer-detail-info-item">
                      <span className="customer-detail-info-label">Email</span>
                      <span className="customer-detail-info-value">
                        {selectedCustomer.email}
                        <span style={{
                          marginLeft: '10px',
                          display: 'inline-block',
                          padding: '2px 8px',
                          fontSize: '0.7rem',
                          background: 'transparent',
                          color: selectedCustomer.isEmailVerified ? '#28a745' : '#856404',
                          border: `1px solid ${selectedCustomer.isEmailVerified ? '#28a745' : '#ffc107'}`,
                          borderRadius: 4,
                          fontWeight: 500,
                          textTransform: 'uppercase',
                          letterSpacing: '0.3px'
                        }}>
                          {selectedCustomer.isEmailVerified ? 'Verified' : 'Unverified'}
                        </span>
                      </span>
                    </div>
                    <div className="customer-detail-info-item">
                      <span className="customer-detail-info-label">Member Since</span>
                      <span className="customer-detail-info-value">
                        {new Date(selectedCustomer.createdAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'long', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                  </div>
                </div>

                    <div className="customer-detail-section">
                  <h3 className="customer-detail-section-title">Account Statistics</h3>
                  <div className="customer-detail-info-grid">
                    <div className="customer-detail-info-item">
                      <span className="customer-detail-info-label">Loyalty Points</span>
                      <span className="customer-detail-info-value" style={{ color: '#7a9c3a', fontSize: '1.1rem' }}>
                        ⭐ {selectedCustomer.points}
                      </span>
                    </div>
                    <div className="customer-detail-info-item">
                      <span className="customer-detail-info-label">Saved Addresses</span>
                      <span className="customer-detail-info-value">{selectedCustomer.addressesCount}</span>
                    </div>
                    <div className="customer-detail-info-item">
                      <span className="customer-detail-info-label">Active Vouchers</span>
                      <span className="customer-detail-info-value">{selectedCustomer.vouchersCount}</span>
                    </div>
                    <div className="customer-detail-info-item">
                      <span className="customer-detail-info-label">Total Orders</span>
                      <span className="customer-detail-info-value">{selectedCustomer.ordersCount || 0}</span>
                    </div>
                  </div>

                    {selectedCustomer.isDeleted && (
                      <div className="customer-detail-section" style={{ marginTop: 16 }}>
                        <div style={{
                          background: '#fef2f2',
                          border: '1.5px solid #fecaca',
                          borderRadius: 12,
                          padding: 14,
                          color: '#7f1d1d',
                          fontWeight: 600
                        }}>
                          This account was deleted on {selectedCustomer.deletedAt ? new Date(selectedCustomer.deletedAt).toLocaleString() : '—'}.
                        </div>
                      </div>
                    )}
                </div>

                {/* Danger Zone */}
                {!selectedCustomer.isAdmin && !selectedCustomer.isDeleted && (
                  <div className="customer-detail-section" style={{ marginTop: 16 }}>
                    <h3 className="customer-detail-section-title" style={{ color: '#991b1b' }}>Danger Zone</h3>
                    <div style={{
                      background: '#fef2f2',
                      border: '1.5px solid #fecaca',
                      borderRadius: 12,
                      padding: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10
                    }}>
                      <div style={{ color: '#7f1d1d', fontWeight: 600 }}>
                        Deleting a customer will:
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 20, color: '#7f1d1d' }}>
                        <li>Disable their ability to login</li>
                        <li>Hide them from active customer lists</li>
                        <li>Keep their orders and history intact</li>
                      </ul>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => {
                          setDeleteCustomerData(selectedCustomer);
                          setShowDeleteModal(true);
                        }}
                        style={{
                          alignSelf: 'flex-start',
                          background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 10,
                          padding: '10px 16px',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        🗑️ Delete Customer
                      </motion.button>
                    </div>
                  </div>
                )}

                {/* Restore Section for Deleted Accounts */}
                {selectedCustomer.isDeleted && (
                  <div className="customer-detail-section" style={{ marginTop: 16 }}>
                    <h3 className="customer-detail-section-title" style={{ color: '#065f46' }}>Restore Account</h3>
                    <div style={{
                      background: '#ecfdf5',
                      border: '1.5px solid #a7f3d0',
                      borderRadius: 12,
                      padding: 16,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10
                    }}>
                      <div style={{ color: '#064e3b', fontWeight: 600 }}>
                        Restore this customer to re-enable login and move them back to active customers.
                      </div>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={async () => {
                          try {
                            const sure = window.confirm(`Restore ${selectedCustomer.name}? They will be reinstated immediately.`);
                            if (!sure) return;
                            const res = await axios.patch(
                              `${process.env.REACT_APP_API}/api/users/admin/customers/${selectedCustomer.id}/restore`,
                              {},
                              { headers: { Authorization: `Bearer ${token}` } }
                            );
                            if (res.status !== 200) throw new Error(res.data?.message || 'Failed to restore customer');
                            alert('Customer restored successfully');

                            // Close modal and refresh lists
                            setSelectedCustomer(null);
                            try {
                              const ref = await axios.get(`${process.env.REACT_APP_API}/api/users/admin/customers`, { headers: { Authorization: `Bearer ${token}` } });
                              setStats(ref.data.stats || null);
                              setAllUsers(ref.data.users || []);
                              setUsers((ref.data.users || []).filter(u => (userType === 'customers' ? !u.isAdmin : userType === 'admins' ? u.isAdmin : true)));
                            } catch {}
                            // Refresh deleted cache so tab counts update
                            try { setDeletedUsers([]); await fetchDeleted(); } catch {}
                          } catch (err) {
                            alert(err.response?.data?.message || err.message || 'Failed to restore customer');
                          }
                        }}
                        style={{
                          alignSelf: 'flex-start',
                          background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 10,
                          padding: '10px 16px',
                          fontWeight: 700,
                          cursor: 'pointer'
                        }}
                      >
                        ♻️ Restore Customer
                      </motion.button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <ConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteCustomerData(null);
        }}
        onConfirm={confirmDeleteCustomer}
        title="Delete Customer"
        message={deleteCustomerData ? `Are you sure you want to delete ${deleteCustomerData.name}? This action can be reversed by support only.` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      />
    </div>
  );
}

export default AdminCustomersPage;
